import { Link, useLocation } from 'react-router-dom'
import { Box, Button, Flex, Img, Text } from '@chakra-ui/react'
import { ArrowUpRightIcon } from '@heroicons/react/24/outline'
import { useIntl } from 'react-intl'
import { useAuthState } from '../../auth/AuthProvider'
import { useAccount } from '../../client/modules/account/AccountProvider'

interface LandingNavbarProps {
  onFeaturesClick?: () => void
  onDemoClick?: () => void
  /** Hidden on Arabic-only surfaces such as the blog, where there is nothing to switch to. */
  hideLocaleToggle?: boolean
}

export default function LandingNavbar({
  onFeaturesClick,
  onDemoClick,
  hideLocaleToggle = false
}: LandingNavbarProps) {
  const { pathname } = useLocation()
  const { isAuthenticated } = useAuthState()
  const { locale, setLocale } = useAccount()
  const { formatMessage } = useIntl()

  let featuresHref = '/#features'
  let demoHref = 'https://calendar.notion.so/meet/mostafaelgayar/l72vw4ohd'
  if (pathname === '/') {
    featuresHref = '#features'
    demoHref = '#book-a-demo'
  }

  return (
    <Box
      as="nav"
      position="fixed"
      insetInline={0}
      top={0}
      zIndex={100}
      bg="rgba(255, 255, 255, 0.94)"
      borderBottom="1px solid"
      borderColor="gray.200"
      sx={{
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)'
      }}
    >
      <Flex
        h="64px"
        maxW="1200px"
        mx="auto"
        px={{ base: 4, md: 6 }}
        align="center"
        justify="space-between"
      >
        <Flex
          as={Link}
          to="/"
          align="center"
          gap={2.5}
          color="gray.900"
          textDecoration="none"
        >
          <Img src="/sijilo.png" alt="" w="32px" h="32px" objectFit="contain" />
          <Text fontSize="sm" fontWeight="750" letterSpacing="-0.02em">
            Sijilo
          </Text>
        </Flex>

        <Flex
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          gap={1}
          align="center"
          display={{ base: 'none', lg: 'flex' }}
        >
          <Button
            as="a"
            href={featuresHref}
            onClick={onFeaturesClick}
            size="sm"
            variant="ghost"
            color="gray.600"
            fontWeight="500"
          >
            {formatMessage({
              id: 'landing.nav.features',
              defaultMessage: 'Features'
            })}
          </Button>
          <Button
            as={Link}
            to="/growth-charts"
            size="sm"
            variant="ghost"
            color="gray.600"
            fontWeight="500"
          >
            {formatMessage({
              id: 'landing.nav.growthCharts',
              defaultMessage: 'Growth charts'
            })}
            <Box
              as="span"
              ms={2}
              px={1.5}
              py={0.5}
              borderRadius="5px"
              bg="gray.100"
              color="gray.600"
              fontSize="9px"
              fontWeight="700"
              letterSpacing="0.06em"
            >
              {formatMessage({
                id: 'landing.nav.free',
                defaultMessage: 'Free'
              })}
            </Box>
          </Button>
        </Flex>

        <Flex gap={1.5} align="center">
          {!hideLocaleToggle && (
            <Button
              size="sm"
              variant="ghost"
              px={2.5}
              color="gray.600"
              aria-label={
                locale === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'
              }
              onClick={() => {
                setLocale(locale === 'en-us' ? 'ar' : 'en-us')
              }}
            >
              {locale === 'ar' ? 'EN' : 'ع'}
            </Button>
          )}
          <Button
            as={Link}
            to={isAuthenticated ? '/dashboard' : '/login'}
            size="sm"
            variant="ghost"
            color="gray.700"
            fontWeight="600"
          >
            {isAuthenticated
              ? formatMessage({
                  id: 'landing.nav.dashboard',
                  defaultMessage: 'Dashboard'
                })
              : formatMessage({
                  id: 'landing.nav.login',
                  defaultMessage: 'Log in'
                })}
          </Button>
          <Button
            as="a"
            href={demoHref}
            target={pathname === '/' ? undefined : '_blank'}
            rel={pathname === '/' ? undefined : 'noopener noreferrer'}
            onClick={onDemoClick}
            size="sm"
            display={{ base: 'none', sm: 'inline-flex' }}
            h="36px"
            px={{ base: 3, md: 4 }}
            bg="gray.900"
            color="white"
            borderRadius="9px"
            fontWeight="650"
            rightIcon={<ArrowUpRightIcon width="14px" />}
            _hover={{ bg: 'black' }}
          >
            {formatMessage({
              id: 'landing.nav.demo',
              defaultMessage: 'Book a demo'
            })}
          </Button>
        </Flex>
      </Flex>
    </Box>
  )
}
